import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FiCopy, FiDownload, FiExternalLink, FiX } from 'react-icons/fi';
import {
  buildPublicPriceListPdfUrl,
  downloadPriceListPdfJob,
  getPriceListPdfJobStatus,
  requestPriceListPdfJob,
} from '../../api';
import useProductSearchSettings from '../../hooks/useProductSearchSettings';
import usePurchaseEvents from '../../hooks/usePurchaseEvents';
import { fieldsMatchProductSearchTerms } from '../../utils/productSearch';
import InlineLoading from '../InlineLoading';

const createState = () => ({
  search: '',
  formatCode: '',
  categoryIds: [],
  includeSoldOut: false,
  error: '',
  shareUrl: '',
  downloadUrl: '',
  copyStatus: '',
});

const resetResult = (current, updates = {}) => ({
  ...current,
  ...updates,
  error: '',
  shareUrl: '',
  downloadUrl: '',
  copyStatus: '',
});

const getErrorDetail = async (error) => {
  const data = error?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    const text = await data.text().catch(() => '');
    if (!text) return '';
    try {
      const parsed = JSON.parse(text);
      return parsed?.error || parsed?.message || parsed?.detail || text;
    } catch (_error) {
      return text;
    }
  }
  if (data && typeof data === 'object') return data.error || data.message || data.detail || '';
  return typeof data === 'string' ? data : error?.message || '';
};

const QuickPriceListPdfDialog = ({
  isOpen,
  branchId,
  catalog = [],
  formatOptions = [],
  categoryOptions = [],
  initialQuery = '',
  initialFormatCode = '',
  initialCategoryId = '',
  onClose,
}) => {
  const generatedId = useId().replace(/:/g, '');
  const titleId = `${generatedId}-title`;
  const descriptionId = `${generatedId}-description`;
  const panelRef = useRef(null);
  const closeButtonRef = useRef(null);
  const formatFieldsetRef = useRef(null);
  const categoryFieldsetRef = useRef(null);
  const focusedFormatCodeRef = useRef(null);
  const focusedCategoryIdRef = useRef(null);
  const previousFocusRef = useRef(null);
  const generationVersionRef = useRef(0);
  const pendingJobRef = useRef('');
  const pollTimerRef = useRef(null);
  const pollAttemptsRef = useRef(0);
  const wasOpenRef = useRef(false);
  const loadingRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const { searchMode } = useProductSearchSettings();
  const [state, setState] = useState(createState);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadingRef.current = loading;
    onCloseRef.current = onClose;
  }, [loading, onClose]);

  const clearPendingJob = useCallback(() => {
    pendingJobRef.current = '';
    pollAttemptsRef.current = 0;
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    generationVersionRef.current += 1;
    clearPendingJob();
  }, [clearPendingJob]);

  useLayoutEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      generationVersionRef.current += 1;
      clearPendingJob();
      setLoading(false);
      return;
    }
    const validCategoryIds = new Set(categoryOptions.map((category) => String(category.id)));
    if (!wasOpenRef.current) {
      wasOpenRef.current = true;
      setState({
        ...createState(),
        search: initialQuery,
        formatCode: formatOptions.some((option) => option.value === initialFormatCode)
          ? initialFormatCode
          : '',
        categoryIds: validCategoryIds.has(String(initialCategoryId))
          ? [String(initialCategoryId)]
          : [],
      });
      return;
    }
    const formatCode = formatOptions.some((option) => option.value === state.formatCode)
      ? state.formatCode
      : '';
    const categoryIds = state.categoryIds.filter((id) => validCategoryIds.has(String(id)));
    if (formatCode === state.formatCode && categoryIds.length === state.categoryIds.length) return;
    generationVersionRef.current += 1;
    clearPendingJob();
    setLoading(false);
    setState((current) => resetResult(current, { formatCode, categoryIds }));
  }, [
    categoryOptions,
    clearPendingJob,
    formatOptions,
    initialCategoryId,
    initialFormatCode,
    initialQuery,
    isOpen,
    state.categoryIds,
    state.formatCode,
  ]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const focusedFormatCode = focusedFormatCodeRef.current;
    if (focusedFormatCode
      && !formatOptions.some((option) => option.value === focusedFormatCode)) {
      focusedFormatCodeRef.current = null;
      const fieldset = formatFieldsetRef.current;
      (fieldset?.querySelector('button:not([disabled])') || fieldset)?.focus();
    }
    const focusedCategoryId = focusedCategoryIdRef.current;
    if (focusedCategoryId
      && !categoryOptions.some((option) => String(option.id) === focusedCategoryId)) {
      focusedCategoryIdRef.current = null;
      const fieldset = categoryFieldsetRef.current;
      (fieldset?.querySelector('[data-pdf-category-id]:not([disabled])') || fieldset)?.focus();
    }
  }, [categoryOptions, formatOptions, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    previousFocusRef.current = document.activeElement;
    const frame = window.requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel || panel.contains(document.activeElement)) return;
      closeButtonRef.current?.focus();
    });
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (loadingRef.current) return;
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(panelRef.current?.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) || []).filter((element) => element.getAttribute('aria-hidden') !== 'true');
      if (!focusable.length) {
        event.preventDefault();
        panelRef.current?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('keydown', handleKeyDown, true);
      if (previousFocusRef.current instanceof HTMLElement
        && document.contains(previousFocusRef.current)) {
        previousFocusRef.current.focus();
      }
    };
  }, [isOpen]);

  const selectedFormat = useMemo(
    () => formatOptions.find((option) => option.value === state.formatCode) || null,
    [formatOptions, state.formatCode]
  );
  const selectedCategoryIds = useMemo(() => {
    const validIds = new Set(categoryOptions.map((category) => String(category.id)));
    return state.categoryIds.map(String).filter((id) => validIds.has(id));
  }, [categoryOptions, state.categoryIds]);
  const hasRequiredSelections = Boolean(selectedFormat && selectedCategoryIds.length);

  const matchingCount = useMemo(() => {
    if (!hasRequiredSelections) return null;
    const selectedCategories = new Set(selectedCategoryIds);
    let count = 0;
    catalog.forEach((product) => {
      if (!selectedCategories.has(String(product.category?.id ?? 'uncategorized'))) return;
      product.varieties.forEach((variety) => variety.formats.forEach((format) => {
        if (format.productFormatActive === false) return;
        if ((format.productFormatCode || format.clientType) !== state.formatCode) return;
        if (!state.includeSoldOut && !format.isAvailable) return;
        if (state.search.trim() && !fieldsMatchProductSearchTerms([
          product.name,
          variety.name,
          format.barcode,
        ], state.search, { searchMode })) return;
        count += 1;
      }));
    });
    return count;
  }, [
    catalog,
    hasRequiredSelections,
    searchMode,
    selectedCategoryIds,
    state.formatCode,
    state.includeSoldOut,
    state.search,
  ]);

  const setFailure = useCallback((detail, fallback = 'No se pudo generar el PDF.') => {
    const normalized = String(detail || '').trim();
    const message = normalized && normalized !== fallback ? `${fallback} ${normalized}` : fallback;
    setState((current) => ({ ...current, error: message }));
  }, []);

  const downloadByJob = useCallback(async (
    jobId,
    knownStatus = null,
    generationVersion = generationVersionRef.current
  ) => {
    if (!jobId || !branchId || generationVersion !== generationVersionRef.current) return;
    try {
      const status = knownStatus || await getPriceListPdfJobStatus(branchId, jobId);
      if (generationVersion !== generationVersionRef.current) return;
      if (!status?.publicAccessToken) throw new Error('El PDF no tiene un enlace firmado.');
      const blob = await downloadPriceListPdfJob(branchId, jobId);
      if (generationVersion !== generationVersionRef.current) return;
      const objectUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `lista_precios_${branchId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
      setState((current) => ({
        ...current,
        error: '',
        copyStatus: '',
        shareUrl: buildPublicPriceListPdfUrl(branchId, jobId, status.publicAccessToken),
        downloadUrl: buildPublicPriceListPdfUrl(
          branchId,
          jobId,
          status.publicAccessToken,
          { download: true }
        ),
      }));
    } catch (error) {
      const detail = await getErrorDetail(error);
      if (generationVersion === generationVersionRef.current) setFailure(detail);
    } finally {
      if (generationVersion === generationVersionRef.current) {
        setLoading(false);
        clearPendingJob();
      }
    }
  }, [branchId, clearPendingJob, setFailure]);

  const startPolling = useCallback((jobId, generationVersion = generationVersionRef.current) => {
    clearPendingJob();
    pendingJobRef.current = jobId;
    pollTimerRef.current = window.setInterval(async () => {
      if (pendingJobRef.current !== jobId || generationVersion !== generationVersionRef.current) return;
      pollAttemptsRef.current += 1;
      try {
        const result = await getPriceListPdfJobStatus(branchId, jobId);
        if (generationVersion !== generationVersionRef.current) return;
        if (result?.status === 'ready') {
          await downloadByJob(jobId, result, generationVersion);
          return;
        }
        if (result?.status === 'error') {
          setFailure(result.error || result.message || result.detail);
          setLoading(false);
          clearPendingJob();
          return;
        }
      } catch (_error) {
        // The realtime event can still complete the job; retry until the bounded timeout.
      }
      if (generationVersion !== generationVersionRef.current) return;
      if (pollAttemptsRef.current >= 30) {
        setFailure('', 'El PDF está tardando demasiado. Intenta nuevamente.');
        setLoading(false);
        clearPendingJob();
      }
    }, 4000);
  }, [branchId, clearPendingJob, downloadByJob, setFailure]);

  const handleBranchEvent = useCallback((event) => {
    if (!pendingJobRef.current) return;
    if (event?.type === 'price-list-pdf-ready' && event.jobId === pendingJobRef.current) {
      void downloadByJob(event.jobId);
    } else if (event?.type === 'price-list-pdf-error' && event.jobId === pendingJobRef.current) {
      setFailure(event.error || event.message || event.detail);
      setLoading(false);
      clearPendingJob();
    }
  }, [clearPendingJob, downloadByJob, setFailure]);

  usePurchaseEvents(branchId, handleBranchEvent, { enabled: Boolean(branchId && isOpen) });

  const generatePdf = async () => {
    if (!selectedFormat) {
      setState((current) => ({ ...current, error: 'Selecciona un formato.' }));
      return;
    }
    if (!selectedCategoryIds.length) {
      setState((current) => ({ ...current, error: 'Selecciona al menos una categoría.' }));
      return;
    }
    if (!matchingCount) {
      setState((current) => ({ ...current, error: 'No hay resultados para exportar.' }));
      return;
    }
    setLoading(true);
    clearPendingJob();
    const generationVersion = ++generationVersionRef.current;
    setState((current) => resetResult(current));
    try {
      const result = await requestPriceListPdfJob(branchId, {
        productFormatCode: state.formatCode,
        clientType: state.formatCode,
        allFormats: false,
        ...(selectedFormat?.productFormatId !== null
          && selectedFormat?.productFormatId !== undefined
          ? { productFormatId: selectedFormat.productFormatId }
          : {}),
        category: selectedCategoryIds[0],
        categories: selectedCategoryIds,
        search: state.search.trim() || undefined,
        searchMode,
        includeImages: 1,
        includeSoldOut: state.includeSoldOut,
      });
      if (generationVersion !== generationVersionRef.current) return;
      if (!result?.jobId) throw new Error('El servidor no entregó el identificador del PDF.');
      if (result.status === 'ready') {
        await downloadByJob(result.jobId, null, generationVersion);
      } else {
        startPolling(result.jobId, generationVersion);
      }
    } catch (error) {
      const detail = await getErrorDetail(error);
      if (generationVersion === generationVersionRef.current) {
        setFailure(detail);
        setLoading(false);
        clearPendingJob();
      }
    }
  };

  const copyShareLink = async () => {
    if (!state.shareUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(state.shareUrl);
      } else {
        const input = document.createElement('textarea');
        input.value = state.shareUrl;
        input.setAttribute('readonly', '');
        input.style.position = 'absolute';
        input.style.left = '-9999px';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        input.remove();
      }
      setState((current) => ({ ...current, copyStatus: 'Copiado' }));
    } catch (_error) {
      setState((current) => ({ ...current, copyStatus: 'No se pudo copiar' }));
    }
  };

  if (!isOpen) return null;
  const selectedCategorySet = new Set(state.categoryIds.map(String));

  return (
    <div className="fixed inset-0 z-[1800] flex items-start justify-center overflow-y-auto bg-slate-950/60 px-3 py-6 sm:px-4">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        onFocusCapture={(event) => {
          focusedFormatCodeRef.current = event.target.dataset.pdfFormatCode || null;
          focusedCategoryIdRef.current = event.target.dataset.pdfCategoryId || null;
        }}
        className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <h2 id={titleId} className="text-lg font-bold text-slate-950">Generar PDF</h2>
            <p id={descriptionId} className="mt-1 text-sm text-slate-600">
              Confirma los filtros antes de descargar o compartir la lista.
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            aria-label="Cerrar generación de PDF"
            disabled={loading}
            onClick={onClose}
            className="rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50"
          >
            <FiX aria-hidden="true" />
          </button>
        </header>

        <div className="space-y-4 px-5 py-4">
          <label className="block">
            <span className="mb-1 block text-sm font-semibold text-slate-700">Búsqueda</span>
            <input
              type="search"
              disabled={loading}
              value={state.search}
              onChange={(event) => setState((current) => resetResult(current, { search: event.target.value }))}
              placeholder="Producto, variedad o código"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </label>

          <fieldset ref={formatFieldsetRef} tabIndex={-1}>
            <legend className="mb-2 text-sm font-semibold text-slate-700">Formatos</legend>
            <div className="flex flex-wrap gap-2">
              {formatOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  disabled={loading}
                  title={option.label}
                  data-pdf-format-code={option.value}
                  aria-pressed={state.formatCode === option.value}
                  onClick={() => setState((current) => resetResult(current, {
                    formatCode: current.formatCode === option.value ? '' : option.value,
                  }))}
                  className={`max-w-[14rem] truncate rounded-full border px-3 py-1.5 text-xs font-bold disabled:opacity-60 ${
                    state.formatCode === option.value
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {!formatOptions.length && <p className="text-sm text-slate-500">No hay formatos disponibles.</p>}
          </fieldset>

          <fieldset ref={categoryFieldsetRef} tabIndex={-1}>
            <legend className="text-sm font-semibold text-slate-700">Categorías</legend>
            <div className="mb-2 mt-2 flex justify-start sm:-mt-6 sm:justify-end">
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => setState((current) => resetResult(current, {
                    categoryIds: [],
                  }))}
                  className="rounded border border-slate-300 px-2.5 py-1 text-xs font-bold text-slate-700 hover:bg-slate-50"
                >
                  Limpiar
                </button>
              </div>
            </div>
            {categoryOptions.length ? (
              <div className="flex max-h-44 flex-wrap gap-2 overflow-y-auto rounded-lg border border-slate-200 p-2">
                {categoryOptions.map((category) => {
                  const selected = selectedCategorySet.has(String(category.id));
                  return (
                    <button
                      key={category.id}
                      type="button"
                      disabled={loading}
                      data-pdf-category-id={String(category.id)}
                      aria-pressed={selected}
                      onClick={() => setState((current) => {
                        const nextIds = new Set(current.categoryIds.map(String));
                        if (nextIds.has(String(category.id))) nextIds.delete(String(category.id));
                        else nextIds.add(String(category.id));
                        return resetResult(current, {
                          categoryIds: Array.from(nextIds),
                        });
                      })}
                      className={`rounded-full border px-3 py-1.5 text-xs font-bold disabled:opacity-60 ${
                        selected
                          ? 'border-emerald-600 bg-emerald-600 text-white'
                          : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {category.label}
                    </button>
                  );
                })}
              </div>
            ) : <p className="text-sm text-slate-500">No hay categorías disponibles.</p>}
          </fieldset>

          <label className="flex items-start gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
            <input
              type="checkbox"
              disabled={loading}
              checked={state.includeSoldOut}
              onChange={(event) => setState((current) => resetResult(current, {
                includeSoldOut: event.target.checked,
              }))}
              className="mt-0.5 h-4 w-4"
            />
            <span>
              <span className="block font-semibold text-slate-900">Mostrar agotados</span>
              <span className="block text-xs text-slate-500">Incluye formatos no disponibles en esta sucursal.</span>
            </span>
          </label>

          <p aria-live="polite" className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
            {hasRequiredSelections
              ? `${matchingCount} ${matchingCount === 1 ? 'fila coincide' : 'filas coinciden'} con estos filtros.`
              : 'Selecciona un formato y al menos una categoría para ver los resultados.'}
          </p>

          {state.error && <p role="alert" className="text-sm font-semibold text-red-600">{state.error}</p>}

          {state.shareUrl && (
            <section className="space-y-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
              <div>
                <h3 className="text-sm font-bold text-emerald-950">PDF listo para compartir</h3>
                <p className="text-xs text-emerald-800">El enlace público expira junto con el archivo generado.</p>
              </div>
              <input type="text" readOnly value={state.shareUrl} className="w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs text-slate-700" />
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => void copyShareLink()} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-2 text-sm font-bold text-white hover:bg-emerald-800">
                  <FiCopy aria-hidden="true" /> Copiar enlace
                </button>
                <a href={state.shareUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-sm font-bold text-emerald-800 hover:bg-emerald-100">
                  <FiExternalLink aria-hidden="true" /> Abrir
                </a>
                <a href={state.downloadUrl} className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-sm font-bold text-emerald-800 hover:bg-emerald-100">
                  <FiDownload aria-hidden="true" /> Descargar
                </a>
                <span aria-live="polite" className="self-center text-xs text-emerald-800">{state.copyStatus}</span>
              </div>
            </section>
          )}
        </div>

        <footer className="flex flex-col-reverse gap-2 border-t border-slate-200 px-5 py-4 sm:flex-row sm:justify-end">
          <button type="button" disabled={loading} onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            Cerrar
          </button>
          <button type="button" disabled={loading || !hasRequiredSelections} onClick={() => void generatePdf()} className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300">
            {loading ? <InlineLoading label="Generando PDF" /> : <><FiDownload aria-hidden="true" /> Generar y descargar</>}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default QuickPriceListPdfDialog;
